/**
 * Alpine.js Language Modal Component
 * Global modal for adding languages
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

function languageModal() {
    return {
        // Modal state
        showModal: false,
        showLevelSelection: false,
        searchTerm: '',
        selectedLanguage: null,
        targetEditor: null, // Reference to the language editor that opened this modal
        
        // Initialize event listeners
        init() {
            // Listen for modal open events from language editors
            this.$el.addEventListener('language-modal-open', (event) => {
                this.openModal(event.detail.targetEditor);
            });

            // Lock body scroll when modal is open
            this.$watch('showModal', (open) => {
                if (open) {
                    lockBodyScroll();
                } else {
                    unlockBodyScroll();
                }
            });
        },
        
        // Open modal from a language editor
        openModal(editor) {
            this.targetEditor = editor;
            this.showModal = true;
            this.showLevelSelection = false;
            this.selectedLanguage = null;
            this.searchTerm = '';
            // Focus search input after modal opens
            this.$nextTick(() => {
                this.$refs.languageSearch?.focus();
            });
        },
        
        closeModal() {
            this.showModal = false;
            this.showLevelSelection = false;
            this.selectedLanguage = null;
            this.searchTerm = '';
            this.targetEditor = null;
        },
        
        selectLanguage(code, name) {
            this.selectedLanguage = { code, name };
            this.showLevelSelection = true;
        },
        
        backToLanguageSelection() {
            this.showLevelSelection = false;
            this.selectedLanguage = null;
        },
        
        selectLevel(level) {
            if (this.selectedLanguage && this.targetEditor) {
                // Add language to the target editor
                this.targetEditor.addLanguage({
                    lang: this.selectedLanguage.code,
                    name: this.selectedLanguage.name,
                    level: level
                });
                this.closeModal();
            }
        },
        
        // Computed properties for filtered languages
        get availableLanguages() {
            return this.$store?.languageData?.available || {};
        },
        
        get filteredLanguages() {
            if (!this.targetEditor) return [];
            
            const term = this.searchTerm.toLowerCase();
            const existingCodes = new Set(this.targetEditor.languages.map(l => l.lang));
            
            return Object.entries(this.availableLanguages)
                .filter(([code, name]) => 
                    !existingCodes.has(code) && 
                    (name.toLowerCase().includes(term) || code.toLowerCase().includes(term))
                )
                .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB));
        },
        
        get popularAvailableLanguages() {
            if (!this.targetEditor) return [];
            
            const popularLanguages = this.$store?.languageData?.popular || [
                'EN', 'ES', 'FR', 'DE', 'IT', 'PT', 'RU', 'ZH', 'JA', 'KO', 
                'AR', 'HI', 'NL', 'SV', 'NO', 'DA', 'FI', 'PL', 'TR', 'EL'
            ];
            
            const existingCodes = new Set(this.targetEditor.languages.map(l => l.lang));
            const term = this.searchTerm.toLowerCase();
            
            return popularLanguages
                .filter(code => this.availableLanguages[code] && !existingCodes.has(code))
                .filter(code => {
                    const name = this.availableLanguages[code];
                    return name.toLowerCase().includes(term) || code.toLowerCase().includes(term);
                })
                .sort((a, b) => this.availableLanguages[a].localeCompare(this.availableLanguages[b]));
        }
    };
}

// Export the component factory
export { languageModal };